import {kebabCase} from 'lodash';
import React, {useEffect, useMemo, useState} from 'react';
import {WithRouterProps} from 'react-router';

import {LoadingSpinner} from '@albert-io/atomic';
import {CurriculumArea, FilterOption, Subject} from './StudentIndependentPractice.types';

import {StudentIndependentPracticeCard} from './StudentIndependentPracticeCard';
import {StudentIndependentPracticeDropdown} from './StudentIndependentPracticeDropdown';
import type {SubjectModelV2} from '@albert-io/models';
import TabEmptyState from '../TabEmptyState';
import useIndependentPracticeSubjects from './hooks/useIndependentPracticeSubjects';

function mapSubjectModel(subject: SubjectModelV2): Subject {
  const curriculumAreaModel = subject.getCurriculumArea();
  const curriculumArea: CurriculumArea = {
    id: curriculumAreaModel.getId(),
    name: curriculumAreaModel.getName(),
    slug: kebabCase(curriculumAreaModel.getName())
  };
  return {
    id: subject.getId(),
    name: subject.getName(),
    urlSlug: subject.getUrlSlug(),
    logoUrl: subject.getLogoLocation().href ?? '',
    isAdaptive: subject.isAdaptive(),
    curriculumArea
  };
}

function extractCurriculumAreas(subjectList: SubjectModelV2[]): CurriculumArea[] {
  const curriculumAreaIds = new Set<string>();
  const uniqueCurriculumAreas: CurriculumArea[] = [];
  subjectList.forEach((s) => {
    const ca = s.getCurriculumArea();
    if (!ca.isEmpty() && !curriculumAreaIds.has(ca.getId())) {
      curriculumAreaIds.add(ca.getId());
      uniqueCurriculumAreas.push({
        id: ca.getId(),
        name: ca.getName(),
        slug: kebabCase(ca.getName())
      });
    }
  });
  return uniqueCurriculumAreas;
}

export function StudentIndependentPractice({params}: WithRouterProps) {
  const classroomId: string | undefined = params?.classroomId;
  const [selectedFilter, setSelectedFilter] = useState<FilterOption>('All');

  const {
    areIndependentPracticeSubjectsLoading: isLoading,
    fetchIndependentPracticeSubjects,
    independentPracticeSubjects: subjects
  } = useIndependentPracticeSubjects(classroomId);

  useEffect(() => {
    fetchIndependentPracticeSubjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const curriculumAreas = useMemo(() => extractCurriculumAreas(subjects), [subjects]);

  const filteredSubjects = useMemo(() => {
    const mapped = subjects.map(mapSubjectModel);
    return selectedFilter === 'All'
      ? mapped
      : mapped.filter((s) => s.curriculumArea.name === selectedFilter);
  }, [subjects, selectedFilter]);

  function renderContent() {
    if (isLoading) {
      return <LoadingSpinner bottomMarginLevel={0} size={3} />;
    }

    if (filteredSubjects.length === 0) {
      return <TabEmptyState copy='No subjects available for practice.' icon={['fal', 'books']} />;
    }

    return (
      <div className='grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-x1'>
        {filteredSubjects.map((subject) => (
          <StudentIndependentPracticeCard subject={subject} key={subject.id} />
        ))}
      </div>
    );
  }

  return (
    <div className='w-full px-x2 py-x3 m:px-x4'>
      <div className='mb-x3'>
        <StudentIndependentPracticeDropdown
          curriculumAreas={curriculumAreas}
          selectedFilter={selectedFilter}
          setSelectedFilter={setSelectedFilter}
        />
      </div>
      {renderContent()}
    </div>
  );
}
